import { t, localePrefix } from "@/helper/helper";
import useAddBasket from "@/hooks/common/useAddBasket";
import useDeleteCart from "@/hooks/common/useDeleteCart";
import Link from "next/link";
import styled from "styled-components";

interface Props {
  data: any;
  locale: string;
}

export default function CartTable({ data, locale }: Props) {
  const { id, quantity, image } = data;
  const { qty, price, title, thumbnail, discount, discount_type } =
    data.product;

  const {
    incrementBasket,
    isLoadingIncrementBasket,
    decrementBasket,
    isLoadingDecrementBasket,
  } = useAddBasket();

  const { deleteCard, isLoadingDeleteCard } = useDeleteCart();

  const handleAction = (type: string) => {
    if (type === "minus") {
      if (!isLoadingDecrementBasket) decrementBasket(id);
    } else if (type === "plus") {
      if (!isLoadingIncrementBasket) {
        incrementBasket(id);
      }
    } else {
      if (!isLoadingDeleteCard) deleteCard(id);
    }
  };

  return (
    <div className="cart-table">
      <div className="img-container">
        <img src={`${image || thumbnail}`} alt="product" />
      </div>
      <div className="content">
        <div className="ordered-product">
          <Link
            className="product-name"
            href={`${localePrefix(locale)}/${data.product.slug}`}
          >
            {title}
          </Link>
          <p className="product-count">
            <span className="quantity">
              {quantity} {t("pieces", locale)}
            </span>
            <svg
              width={8}
              height={8}
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 1L1 7"
                stroke="#989EAF"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1 1L7 7"
                stroke="#989EAF"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <BasketPriceBox className="price-section">
              <div className="discount">
                {Math.trunc(-data.discount)}{" "}
                {data.discount_type === "1" ? (
                  <svg
                    className="manatIconX"
                    width={33}
                    height={27}
                    viewBox="0 0 33 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                      fill="#fff"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                      fill="#fff"
                    />
                  </svg>
                ) : (
                  "%"
                )}
              </div>
              <div className="prices">
                <span className="old-price">
                  {price}
                  <svg
                    width={33}
                    height={27}
                    viewBox="0 0 33 27"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="manatIcon"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                      fill="#d0d4de"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                      fill="#d0d4de"
                    />
                  </svg>
                </span>
                <p className="new-price">
                  {discount_type === "1" ? (
                    <>
                      {price - discount}{" "}
                      <svg
                        width={33}
                        height={27}
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="manatIcon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                          fill="#FF4C4C"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                          fill="#FF4C4C"
                        />
                      </svg>
                    </>
                  ) : (
                    <>
                      {price - ((price * discount) / 100 || 0)}{" "}
                      <svg
                        width={33}
                        height={27}
                        viewBox="0 0 33 27"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="manatIcon"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M15 26L15.8511 1.03704L18.6596 0L19 24L15 26Z"
                          fill="#FF4C4C"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0 26.7126L3.31441 26.8563C3.37837 10.7044 12.0742 5.37873 16.3559 5.22923C27.9253 5.59751 29.2515 20.3484 29.6856 26.8563L33 27C32.7507 11.4088 24.1817 2.85531 17.1485 3.00185C5.15613 3.42337 0.588832 15.6295 0 26.7126Z"
                          fill="#FF4C4C"
                        />
                      </svg>
                    </>
                  )}
                </p>
              </div>
            </BasketPriceBox>
          </p>
        </div>
        <div className="cart-table-functions">
          <div className="cart-table-delete-quatity">
            <div className="quantity-container">
              <button className="minus" onClick={() => handleAction("minus")}>
                <svg
                  width={10}
                  height={2}
                  viewBox="0 0 10 2"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 1H9"
                    stroke="#3A4155"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="quantity">{quantity}</span>
              <button
                className="plus"
                onClick={() => handleAction("plus")}
                disabled={qty === quantity}
              >
                <svg
                  width={10}
                  height={10}
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 1V9"
                    stroke="#3A4155"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 5H9"
                    stroke="#3A4155"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <button
              className="delete-btn"
              onClick={() => handleAction("delete")}
            >
              <svg
                width={14}
                height={15}
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 3.66699H2.33333H13"
                  stroke="#3A4155"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.33301 3.66667V2.33333C4.33301 1.97971 4.47348 1.64057 4.72353 1.39052C4.97358 1.14048 5.31272 1 5.66634 1H8.33301C8.68663 1 9.02577 1.14048 9.27582 1.39052C9.52587 1.64057 9.66634 1.97971 9.66634 2.33333V3.66667M11.6663 3.66667V13C11.6663 13.3536 11.5259 13.6928 11.2758 13.9428C11.0258 14.1929 10.6866 14.3333 10.333 14.3333H3.66634C3.31272 14.3333 2.97358 14.1929 2.72353 13.9428C2.47348 13.6928 2.33301 13.3536 2.33301 13V3.66667H11.6663Z"
                  stroke="#3A4155"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.66699 7V11"
                  stroke="#3A4155"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.33301 7V11"
                  stroke="#3A4155"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
          {qty === quantity && (
            <p className="error-message">{t("maxStok", locale)}</p>
          )}
        </div>
      </div>
    </div>
  );
}

const BasketPriceBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-top: 2.4rem;
  padding-bottom: 1.8rem;

  .manatIconX {
    margin: 0 !important;
    margin-left: 3px !important;
    height: 15px !important;
    width: 10px !important;
  }

  .discount {
    padding: 4px;
    background: linear-gradient(223.94deg, #fb0944 0.88%, #ff4572 97.34%);
    border-radius: 6px;
    border: 5px solid #ff2459;
    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.25);
    margin-right: 1.8rem;
  }

  .prices {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    svg{
      margin-left: 5px !important;
    }
  }

  .old-price {
    display: flex;
    align-items: center;
    font-size: 14px;
    margin-right: 0 !important;
    svg {
      width: 14px !important;
      height: 15px !important;
      margin-left: 5px !important;
      margin-top: -5px !important;
    }
  }

  .new-price {
    display: flex;
    align-items: center;
    font-size: 20px;

    svg {
      margin-left: 10px !important;
      width: 20px !important;
      height: 20px !important;
      margin-right: 0 !important;
    }
  }

  p {
    margin-bottom: 0;
  }
`;
